import React from "react";
import { format, formatDistanceToNow } from "date-fns";
import {
  TableStatus,
  TableStatusConfig,
  TableAvatar,
  TableTag,
  TableColumn,
  TableDateTimeFormat,
  TableSortConfig,
  WithId,
} from "../../app.interface";

// Default configurations
export const defaultStatuses: TableStatusConfig = {
  active: { label: "Active", color: "green" },
  inactive: { label: "Inactive", color: "gray" },
  pending: { label: "Pending", color: "yellow" },
  processing: { label: "Processing", color: "blue" },
  error: { label: "Error", color: "rose" },
};

export const defaultDateFormat: TableDateTimeFormat = "absolute";
export const defaultCustomDateFormat = "yyyy-MM-dd HH:mm";

// Base text styles
const baseTextStyles = "font-['Montserrat'] text-sm";
const baseLabelStyles = "font-['Montserrat'] text-sm font-medium";

// Utility function to combine CSS classes
export const classNames = (...classes: string[]): string => {
  return classes.filter(Boolean).join(" ");
};

// Status Cell Renderer
export const renderStatusCell = (
  value: string,
  statuses: TableStatusConfig = defaultStatuses
): React.ReactElement => {
  const statusConfig =
    statuses[value?.toLowerCase()] || defaultStatuses.inactive;
  const colorClasses = {
    green: "text-green-400 bg-green-400/10",
    rose: "text-rose-400 bg-rose-400/10",
    yellow: "text-yellow-400 bg-yellow-400/10",
    blue: "text-blue-400 bg-blue-400/10",
    gray: "text-gray-400 bg-gray-400/10",
  };

  return (
    <div className="flex items-center gap-x-2">
      <div
        className={classNames(
          colorClasses[statusConfig.color],
          "flex-none rounded-full p-1"
        )}
      >
        <div className="h-1.5 w-1.5 rounded-full bg-current" />
      </div>
      <span className={classNames(baseLabelStyles, "text-gray-900")}>
        {statusConfig.label}
      </span>
    </div>
  );
};

// Avatar Cell Renderer
export const renderAvatarCell = (avatar: TableAvatar): React.ReactElement => (
  <div className="flex items-center gap-x-4">
    {avatar.imageUrl ? (
      <img
        src={avatar.imageUrl}
        alt=""
        className="h-8 w-8 rounded-full bg-gray-50"
      />
    ) : (
      <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
        <span className={classNames(baseLabelStyles, "text-gray-600")}>
          {avatar.name.charAt(0).toUpperCase()}
        </span>
      </div>
    )}
    <div className={classNames(baseLabelStyles, "truncate text-gray-900")}>
      {avatar.name}
    </div>
  </div>
);

// Tag Cell Renderer
export const renderTagCell = (tag: TableTag): React.ReactElement => (
  <div className="flex items-center gap-x-2">
    <span
      className={classNames(
        baseTextStyles,
        "inline-flex items-center rounded-md px-2 py-1 ring-1 ring-inset",
        tag.color || "bg-gray-50 text-gray-600 ring-gray-500/10"
      )}
    >
      {tag.label}
    </span>
  </div>
);

// DateTime Cell Renderer
export const renderDateTimeCell = (
  value: string | number,
  dateFormat: TableDateTimeFormat = defaultDateFormat,
  customFormat: string = defaultCustomDateFormat
): React.ReactElement => {
  const date = typeof value === "number" ? new Date(value) : new Date(value);

  return (
    <span className={baseTextStyles}>
      {(() => {
        switch (dateFormat) {
          case "relative":
            return formatDistanceToNow(date, { addSuffix: true });
          case "absolute":
            return format(date, "PPpp");
          case "custom":
            return format(date, customFormat);
          default:
            return value;
        }
      })()}
    </span>
  );
};

// Text Cell Renderer
export const renderTextCell = (value: any): React.ReactElement => (
  <span className={classNames(baseTextStyles, "text-gray-900")}>
    {String(value)}
  </span>
);

// Money Cell Renderer
export const renderMoneyCell = (value: string | number): React.ReactElement => (
  <span
    className={classNames(baseTextStyles, "whitespace-nowrap text-gray-900")}
  >
    {typeof value === "string"
      ? value
      : new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "BHD",
        }).format(value)}
  </span>
);

// Action Cell Renderer
export const renderActionCell = (
  row: Record<string, any>,
  renderAction?: (row: WithId & Record<string, any>) => React.ReactElement
): React.ReactElement => {
  if (renderAction && "_id" in row) {
    return renderAction(row as WithId & Record<string, any>);
  }
  return (
    <a
      href="#"
      className={classNames(
        baseLabelStyles,
        "text-indigo-600 hover:text-indigo-900"
      )}
    >
      Edit<span className="sr-only">, {row.name || row.id}</span>
    </a>
  );
};

// Sort Header Renderer
export const renderSortHeader = (
  column: TableColumn,
  sortConfig?: TableSortConfig,
  onSort?: (field: string, direction: "asc" | "desc") => void
): React.ReactElement => {
  const isSorted = sortConfig?.field === column.field;
  const isAscending = isSorted && sortConfig?.direction === "asc";

  return (
    <button
      type="button"
      onClick={() => onSort?.(column.field, isAscending ? "desc" : "asc")}
      className={classNames(
        baseLabelStyles,
        "group inline-flex items-center text-left"
      )}
    >
      {column.header}
      <span
        className={classNames(
          "ml-2 flex-none rounded-sm",
          isSorted
            ? "bg-gray-100 text-gray-900"
            : "invisible text-gray-400 group-hover:visible group-focus:visible"
        )}
      >
        <svg
          className={classNames("h-5 w-5", isAscending ? "rotate-180" : "")}
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </span>
    </button>
  );
};

// Contact Info Cell Renderer
export const renderContactInfoCell = (
  name: string,
  email?: string,
  avatar?: string
): React.ReactElement => (
  <div className="flex items-center">
    {avatar ? (
      <div className="size-10 shrink-0">
        <img alt="" src={avatar} className="size-10 rounded-full" />
      </div>
    ) : (
      <div className="size-10 shrink-0 rounded-full bg-gray-100 flex items-center justify-center">
        <span className={classNames(baseLabelStyles, "text-gray-600")}>
          {name.charAt(0).toUpperCase()}
        </span>
      </div>
    )}
    <div className="ml-4">
      <div className={classNames(baseLabelStyles, "text-gray-900")}>{name}</div>
      {email && (
        <div className={classNames(baseTextStyles, "mt-1 text-gray-500")}>
          {email}
        </div>
      )}
    </div>
  </div>
);

// Main Cell Renderer
export const renderTableCell = (
  row: Record<string, any>,
  column: TableColumn
): React.ReactElement => {
  const value = column.accessor
    ? typeof column.accessor === "function"
      ? column.accessor(row)
      : row[column.accessor]
    : row[column.field];

  switch (column.type) {
    case "status":
      return renderStatusCell(value, column.statusConfig);
    case "avatar":
      return renderAvatarCell(value);
    case "tag":
      return renderTagCell(value);
    case "datetime":
      return renderDateTimeCell(
        value,
        column.dateFormat,
        column.customDateFormat
      );
    case "money":
      return renderMoneyCell(value);
    case "action":
      return renderActionCell(row, column.renderAction);
    case "contact":
      return renderContactInfoCell(
        column.contactName ? row[column.contactName] : value?.name || value,
        column.contactEmail ? row[column.contactEmail] : value?.email,
        column.contactAvatar ? row[column.contactAvatar] : value?.avatar
      );
    default:
      return renderTextCell(value);
  }
};
